import { Bell, Mail, FileText, Users, Info, Check, Trash2, MessageSquare, CreditCard, CalendarClock, CheckSquare, LayoutGrid, AlertTriangle, MailX, MailOpen, MousePointerClick, Send, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotifications, UserNotification } from "@/hooks/useNotifications";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { sanitizeActivityText } from "@/lib/sanitizeActivityText";

const toPlainText = sanitizeActivityText;

const notificationIcons: Record<UserNotification['type'], React.ComponentType<{ className?: string }>> = {
  email_reply: Mail,
  contract_signed: FileText,
  contract_viewed: FileText,
  contract_comment: MessageSquare,
  contract_payment: CreditCard,
  contract_renewal: CalendarClock,
  contract_reminder: CalendarClock,
  lead_update: Users,
  system: Info,
  task_assigned: CheckSquare,
  task_updated: LayoutGrid,
  task_comment: MessageSquare,
  task_mention: Users,
  usage_warning: AlertTriangle,
  lead_hard_bounced: MailX,
  lead_soft_bounced_final: MailX,
  email_opened: MailOpen,
  email_clicked: MousePointerClick,
  email_scheduled: Clock,
  email_sent: Send,
};

const notificationColors: Record<UserNotification['type'], string> = {
  email_reply: "text-blue-500",
  contract_signed: "text-green-500",
  contract_viewed: "text-orange-500",
  contract_comment: "text-indigo-500",
  contract_payment: "text-emerald-500",
  contract_renewal: "text-amber-500",
  contract_reminder: "text-amber-500",
  lead_update: "text-purple-500",
  system: "text-muted-foreground",
  task_assigned: "text-cyan-500",
  task_updated: "text-teal-500",
  task_comment: "text-violet-500",
  task_mention: "text-pink-500",
  usage_warning: "text-destructive",
  lead_hard_bounced: "text-destructive",
  lead_soft_bounced_final: "text-orange-500",
  email_opened: "text-amber-500",
  email_clicked: "text-emerald-500",
  email_scheduled: "text-sky-500",
  email_sent: "text-indigo-500",
};

export function NotificationBell() {
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  const latestUnread = notifications.find((n) => !n.is_read);

  const handleNotificationClick = async (notification: UserNotification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // Prefer opening the lead profile directly whenever a lead is attached —
    // that's what users expect when they click a lead-scoped notification.
    // Fall back to the notification's explicit link (contract routes, etc.)
    // when there is no lead.
    if (notification.lead_id) {
      navigate(`/leads?lead=${notification.lead_id}`);
    } else if (notification.link) {
      navigate(notification.link);
    }
  };

  const bellButton = (
    <Button variant="ghost" size="icon" className="relative">
      <Bell className="h-5 w-5" />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-medium">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </Button>
  );

  return (
    <TooltipProvider delayDuration={300}>
    <Popover onOpenChange={(open) => { if (open && unreadCount > 0) markAllAsRead(); }}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            {bellButton}
          </PopoverTrigger>
        </TooltipTrigger>
        {latestUnread && (
          <TooltipContent side="bottom" className="max-w-[260px] p-3 bg-popover text-popover-foreground border shadow-lg">
            <p className="text-xs font-semibold mb-1">
              {(latestUnread.lead_name || latestUnread.lead_email)
                ? `${latestUnread.lead_name || latestUnread.lead_email} — ${latestUnread.title}`
                : latestUnread.title}
            </p>
            <p className="text-xs text-muted-foreground line-clamp-2">{toPlainText(latestUnread.message)}</p>
            <p className="text-[10px] text-muted-foreground mt-1">
              {formatDistanceToNow(new Date(latestUnread.created_at), { addSuffix: true })}
            </p>
          </TooltipContent>
        )}
      </Tooltip>
      <PopoverContent className="w-[360px] sm:w-[400px] p-0" align="end">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h4 className="font-semibold text-sm">Notifications</h4>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7"
              onClick={markAllAsRead}
            >
              <Check className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-[360px]">
          {isLoading ? (
            <div className="flex items-center justify-center h-20">
              <span className="text-sm text-muted-foreground">Loading...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-20 text-muted-foreground">
              <Bell className="h-8 w-8 mb-2 opacity-50" />
              <span className="text-sm">No notifications yet</span>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => {
                const Icon = notificationIcons[notification.type];
                const iconColor = notificationColors[notification.type];
                
                return (
                  <div
                    key={notification.id}
                    className={cn(
                      "px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors group",
                      !notification.is_read && "bg-primary/5"
                    )}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex gap-3">
                      <div className={cn("mt-0.5 shrink-0", iconColor)}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            {(notification.lead_name || notification.lead_email) && (
                              <p className="text-[11px] font-semibold text-primary truncate">
                                {notification.lead_name || notification.lead_email}
                                {notification.lead_company ? (
                                  <span className="text-muted-foreground font-normal"> · {notification.lead_company}</span>
                                ) : null}
                              </p>
                            )}
                            <p className={cn(
                              "text-sm font-medium leading-snug",
                              !notification.is_read && "text-foreground",
                              notification.is_read && "text-muted-foreground"
                            )}>
                              {notification.title}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {!notification.is_read && (
                              <div className="w-2 h-2 rounded-full bg-primary" />
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteNotification(notification.id);
                              }}
                            >
                              <Trash2 className="h-3 w-3 text-muted-foreground" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-3 mt-1 leading-relaxed">
                          {toPlainText(notification.message)}
                        </p>
                        <p className="text-[11px] text-muted-foreground/70 mt-1.5">
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
    </TooltipProvider>
  );
}
